import React from "react";
import {
  CfcStepSelectContent,
  CfcSelectIntro,
  CfcConditionList,
  CfcConditionItem,
  CfcConditionLabel,
  CfcCheckbox,
  CfcSectionTitle,
  CfcIndicationBlock,
  CfcIndicationLabel,
  CfcEligibleLead,
  CfcIcd10Section,
  CfcSubsectionHeader,
  CfcSubsectionDescription,
  CfcIcd10Row,
  CfcIcd10Label,
  CfcIcd10Code,
  CfcIcd10OtherLabel,
  CfcIcd10OtherInput,
  CfcPhlebotomyRow,
  CfcPhlebotomyCopy,
  CfcPhlebotomyTitle,
  CfcPhlebotomyDescription,
} from "./CfcStepSelect.styles";
import type { CfcConditionCode, CfcEligibleCondition } from "./cfcConditions";
import type { CfcFormData } from "./types";

const ABNORMAL_ICD10_CODE = "O28.5";
const CFC_ICD10_ABNORMAL_ID = "cfc-icd10-abnormal";
const CFC_REQUEST_PHLEBOTOMY_ID = "cfc-request-phlebotomy";
const INDICATION_COPY = (
  <>
    Patients who previously received a high-risk/detected Unity Aneuploidy NIPT
    test, excluding Monosomy X. Testing can only be performed for singleton
    pregnancies, and cannot be performed for egg donors or gestational carriers.
  </>
);
const ELIGIBLE_CONDITIONS_LEAD = (
  <>The condition(s) shown below are eligible for Unity Confirm:</>
);
const ICD10_DESCRIPTION = (
  <>
    Required to select at least one for each test panel. An ICD-10 code selected
    under one test panel might be used in another test panel&apos;s billing. The
    following codes are not exhaustive.
  </>
);

interface CfcStepSelectProps {
  conditions: ReadonlyArray<CfcEligibleCondition>;
  formData: CfcFormData;
  onFormChange: (updates: Partial<CfcFormData>) => void;
}

export const CfcStepSelect = ({
  conditions,
  formData,
  onFormChange,
}: CfcStepSelectProps): React.ReactElement => {
  const handleConditionChange = (code: CfcConditionCode, checked: boolean) => {
    onFormChange({
      selectedConditions: checked
        ? [...formData.selectedConditions, code]
        : formData.selectedConditions.filter(
            (selectedCode) => selectedCode !== code,
          ),
    });
  };

  return (
    <CfcStepSelectContent>
      <CfcSelectIntro>
        <CfcSectionTitle>Condition Selection</CfcSectionTitle>
        <CfcIndicationBlock>
          <CfcIndicationLabel>Indication: </CfcIndicationLabel>
          {INDICATION_COPY}
        </CfcIndicationBlock>
        <CfcEligibleLead>{ELIGIBLE_CONDITIONS_LEAD}</CfcEligibleLead>
      </CfcSelectIntro>
      <CfcConditionList>
        {conditions.map((condition) => {
          const inputId = `cfc-condition-${condition.code}`;
          const isChecked = formData.selectedConditions.includes(
            condition.code,
          );

          return (
            <CfcConditionItem key={condition.code}>
              <CfcCheckbox
                checked={isChecked}
                inputProps={{ id: inputId }}
                onChange={(e) =>
                  handleConditionChange(condition.code, e.target.checked)
                }
              />
              <CfcConditionLabel htmlFor={inputId}>
                {condition.label}
              </CfcConditionLabel>
            </CfcConditionItem>
          );
        })}
      </CfcConditionList>

      <CfcIcd10Section>
        <CfcSubsectionHeader>ICD-10 Code Selection*</CfcSubsectionHeader>
        <CfcSubsectionDescription>{ICD10_DESCRIPTION}</CfcSubsectionDescription>
        <CfcIcd10Row>
          <CfcCheckbox
            checked={formData.icd10Abnormal}
            inputProps={{ id: CFC_ICD10_ABNORMAL_ID }}
            onChange={(e) => onFormChange({ icd10Abnormal: e.target.checked })}
          />
          <CfcIcd10Label htmlFor={CFC_ICD10_ABNORMAL_ID}>
            Abnormal chromosomal &amp; genetic findings
          </CfcIcd10Label>
          <CfcIcd10Code>{ABNORMAL_ICD10_CODE}</CfcIcd10Code>
        </CfcIcd10Row>
        <CfcIcd10OtherLabel>Other ICD-10 codes</CfcIcd10OtherLabel>
        <CfcIcd10OtherInput
          variant="standard"
          fullWidth
          value={formData.icd10OtherCodes}
          onChange={(e) => onFormChange({ icd10OtherCodes: e.target.value })}
          placeholder=""
        />
      </CfcIcd10Section>

      {formData.isMobilePhlebotomyAccount && (
        <CfcPhlebotomyRow>
          <CfcCheckbox
            checked={formData.isMobilePhlebotomy}
            inputProps={{ id: CFC_REQUEST_PHLEBOTOMY_ID }}
            onChange={(e) =>
              onFormChange({ isMobilePhlebotomy: e.target.checked })
            }
          />
          <CfcPhlebotomyCopy>
            <CfcPhlebotomyTitle htmlFor={CFC_REQUEST_PHLEBOTOMY_ID}>
              Request phlebotomy
            </CfcPhlebotomyTitle>
            <CfcPhlebotomyDescription>
              If this patient requires phlebotomy coordination, please check
              this box and a BillionToOne Client Services representative will
              follow up with the patient to schedule their blood draw.
            </CfcPhlebotomyDescription>
          </CfcPhlebotomyCopy>
        </CfcPhlebotomyRow>
      )}
    </CfcStepSelectContent>
  );
};
